// Pure helpers for Pro Camera devices. Timestamps from the API are Unix seconds unless noted.
export const WAKE_OVERDUE_SECONDS = 5 * 60;
export const AWAKE_SECONDS = 60;

export const DEVICE_STATUSES = [
  { value: "unclaimed", label: "New devices" },
  { value: "stock", label: "Stock" },
  { value: "assigned", label: "Assigned" },
  { value: "returning", label: "Returning" },
  { value: "disabled", label: "Disabled" },
];

const STATUS_CLASSES = {
  unclaimed: "is-primary",
  stock: "is-info",
  assigned: "is-success",
  active: "is-success",
  claimed: "is-info",
  returning: "is-warning",
  pending: "is-warning",
  disabled: "is-danger",
};

export function statusClass(status) {
  return STATUS_CLASSES[status] || "is-light";
}

const clock = (seconds) =>
  new Date(seconds * 1000).toLocaleTimeString("tr-TR", {
    timeZone: "Europe/Istanbul",
    hour: "2-digit",
    minute: "2-digit",
  });

// A sleep report sets sleeping_since and any later request clears it, so it is checked before last_request.
export function devicePower(device, now = Date.now() / 1000) {
  if (device.sleeping_since) {
    if (!device.next_wake_at) return { label: "Sleeping", className: "is-link is-light" };
    const wake = (device.next_wake_confirmed ? "" : "~") + clock(device.next_wake_at);
    if (now - device.next_wake_at > WAKE_OVERDUE_SECONDS) {
      return { label: `Wake overdue (expected ${wake})`, className: "is-danger is-light" };
    }
    return {
      label: `Sleeping · next wake ${wake}${device.next_wake_confirmed ? " ✓" : ""}`,
      className: "is-link is-light",
    };
  }
  if (!device.last_request) return { label: "Never seen", className: "is-light" };
  if (now - device.last_request <= AWAKE_SECONDS) {
    return { label: "Awake now", className: "is-success is-light" };
  }
  return { label: "Off · no sleep report", className: "is-warning is-light" };
}

const RUN_REASONS = {
  unclaimed: "not claimed",
  no_site: "no site",
  device_disabled: "site inactive",
  no_contract: "no contract",
  revoked: "revoked",
  canceled: "contract canceled",
  expired: "contract expired",
  contract_suspended: "contract suspended",
  over_due_payment: "payment overdue",
};

export function runReasonText(reason) {
  return RUN_REASONS[reason] || reason;
}

export const OWNERSHIP_KINDS = {
  rental: "Rental",
  sale: "Sale",
  return: "Return",
  buyback: "Buyback",
  stock: "Stock",
};

export function isAwake(device, now = Date.now() / 1000) {
  return (
    !device.sleeping_since &&
    Boolean(device.last_request) &&
    now - device.last_request <= AWAKE_SECONDS
  );
}

// Mirrors StockProCamera in the backend: wipe evidence after the assignment ended and zero pending files.
// Return and buyback rows have no camera and are not site assignments.
export function returnState(device) {
  const assignment = (device.ownership || []).find(
    (row) => row.camera_id && row.ended_at && !row.stocked_at
  );
  const pendingFiles = device.last_report?.pending_files;
  const wiped = Boolean(
    assignment?.wiped_at &&
      assignment.wipe_received_at &&
      assignment.wiped_at > assignment.ended_at
  );
  return {
    wiped,
    wipedAt: wiped ? assignment.wiped_at : null,
    pendingFiles,
    ready: wiped && pendingFiles === 0,
  };
}

const volts = (adc) => `${((adc * 13.5) / 546).toFixed(1)} V`;

// arduino_sync: "<date> fw=uyan-1 adc=670 | OK T once=... sonra=... | OK W uyanma=..."
export function arduinoSummary(sync) {
  if (!sync) return "";
  const firmware = (sync.match(/fw=(\S+)/) || [])[1];
  const adc = (sync.match(/adc=(\d+)/) || [])[1];
  const times = sync.match(/once=(\S+ \S+) sonra=(\S+ \S+)/);
  if (!firmware) return sync;
  const parts = [firmware];
  if (times) {
    const drift = Math.round(
      (Date.parse(times[1].replace(" ", "T")) - Date.parse(times[2].replace(" ", "T"))) / 1000
    );
    parts.push(`clock ${drift > 0 ? "+" : ""}${drift} s`);
  }
  if (adc) parts.push(`battery ≈ ${volts(Number(adc))}`);
  return parts.join(" · ");
}

function arduinoEventText(type, extra) {
  const value = Number(extra);
  if (type === "B") {
    const reasons = [
      [1, "power on"],
      [2, "external reset or serial port"],
      [4, "brownout"],
      [8, "watchdog"],
    ]
      .filter(([bit]) => value & bit)
      .map(([, text]) => text);
    return `Arduino restarted${reasons.length ? ` (${reasons.join(", ")})` : ""}`;
  }
  if (type === "S") {
    const received = [value & 1 && "clock", value & 2 && "wake time"].filter(Boolean);
    return `Received ${received.join(" and ")} from Pi${value & 4 ? " (limited)" : ""}`;
  }
  if (type === "P") return `Powered Pi on (${value ? "requested time" : "default cycle"})`;
  if (type === "O") return "Powered Pi off";
  if (type === "L") return "Voltage too low, Pi not powered on";
  if (type === "X") return "Pi stayed on over 45 minutes, power forced off";
  return type;
}

// Each event: "LOG <sequence> <type> <extra> <YYYY-MM-DD HH:MM:SS> <adc>", Arduino local time.
export function arduinoEvents(events) {
  return (events || [])
    .map((line) => line.split(" "))
    .filter((parts) => parts[0] === "LOG" && parts.length >= 7)
    .reverse()
    .slice(0, 12)
    .map((parts) => ({
      time: `${parts[4]} ${parts[5]}`,
      text: arduinoEventText(parts[2], parts[3]),
      voltage: volts(Number(parts[6])),
    }));
}
